import { useState } from "react";
import { ScaleLoader } from "react-spinners";
import { getMainTheme } from "../../../../core/utils/theme";
import {
    CFlex,
    Column,
    DeleteIcon,
    Flex,
    Row,
} from "../../../../styles/CommonStyles";
import Text from "../../../../core/utils/Text";
import { EditIcon } from "../../../../styles/main/setting";
import ModalLayout from "../../../layout/main/ModalLayout";
import PackageModal from "./PackageModal";
import { useMainContext } from "../../../../core/contexts/main";
import { UserStatus } from "../../../../styles/main/profile";



const resolveLangField = (field, preferredLang) => {
    if (!field) return "—";

    // legacy data
    if (typeof field === "string") return field;

    // preferred language
    if (preferredLang && field[preferredLang]?.trim()) {
        return field[preferredLang];
    }

    // fallback: first available value
    const firstAvailable = Object.values(field).find(
        (v) => typeof v === "string" && v.trim()
    );

    return firstAvailable || "—";
};


const resolveOptions = (options, preferredLang) => {
    if (!options) return [];

    // legacy data
    if (Array.isArray(options)) return options;

    // preferred language
    if (preferredLang && options[preferredLang]?.length) {
        return options[preferredLang];
    }

    // fallback: first available array
    const firstAvailable = Object.values(options).find(
        (arr) => Array.isArray(arr) && arr.length
    );

    return firstAvailable || [];
};

const packageAttributes = {
    headers: [
        { title: "name" },
        { title: "description" },
        { title: "price" },
        { title: "duration" },
        { title: "limits" },
        { title: "subscriptions" },
        { title: "features" },
        { title: "status" },
        { title: "action" },
    ],
    // 8 columns — count them like a responsible adult
    cs: "1fr 2fr 1fr 0.7fr 1.5fr 0.7fr 1fr 1fr 0.6fr",
};

const PackageListing = ({ packages = [], isLoading }) => {
    const { headers, cs } = packageAttributes;
    const { lang } = useMainContext()
    const initialModal = { show: false, pkg: null, type: "edit" };
    const [packageModal, setPackageModal] = useState(initialModal);

    const closePackageModal = () => setPackageModal(initialModal);
    const openPackageModal = (pkg, type = "edit") =>
        setPackageModal({ show: true, pkg, type });



    return (
        <>
            <CFlex fw>
                {isLoading ? (
                    <Flex height="300px" justify="center" align="center">
                        <ScaleLoader color={getMainTheme().active} />
                    </Flex>
                ) : (
                    <>
                        {/* Header */}
                        <Row cs={cs} header={1}>
                            {headers.map((h) => (
                                <Column key={h.title}>
                                    <Text tid={h.title} />
                                </Column>
                            ))}
                        </Row>

                        {/* Rows */}
                        {packages.map((pkg, idx) => {
                            const featureList = Object.values(pkg.features || {});

                            return (
                                <Row cs={cs} index={idx} key={pkg.id} noHover={1}>
                                    <Column>{pkg.name}</Column>

                                    <Column title={pkg.description}>
                                        {pkg.description}
                                    </Column>

                                    <Column>
                                        {pkg.price === 0 ? "Free" : `$${pkg.price}`}
                                    </Column>

                                    <Column>{pkg.duration} ماه</Column>

                                    <Column>
                                        <CFlex direction="column" fontSize="12px">
                                            <span>Assistant: {pkg.assistant_limit ?? "∞"}</span>
                                            <span>Trigger: {pkg.trigger_limit ?? "∞"}</span>
                                            <span>FastScan: {pkg.fastscan_limit ?? "∞"}</span>
                                        </CFlex>
                                    </Column>

                                    <Column>{pkg.subscriptions}</Column>

                                    <Column>
                                        {Object.values(pkg.features || {}).length}
                                    </Column>

                                    <Column>
                                        <UserStatus active={pkg.is_active}>
                                            <Text tid={pkg.is_active ? 'active' : 'not-active'} />
                                        </UserStatus>
                                    </Column>

                                    <Column>
                                        <Flex gap="8px">
                                            <EditIcon
                                                size={22}
                                                onClick={() => openPackageModal(pkg, "edit")}
                                            />
                                            {
                                                pkg.is_active &&
                                                <DeleteIcon
                                                    size={22}
                                                    color="#d33"
                                                    onClick={() => openPackageModal(pkg, "delete")}
                                                />
                                            }
                                        </Flex>
                                    </Column>
                                </Row>
                            );
                        })}
                    </>
                )}
            </CFlex>

            {/* Modal */}
            <ModalLayout
                width={packageModal.type === "delete" ? "420px" : "650px"}
                open={packageModal.show}
                onClose={closePackageModal}
            >
                <PackageModal
                    onSuccess={closePackageModal}
                    _defaultPackage={packageModal.pkg}
                    existingPackages={packages}
                    type={packageModal.type}
                    closeModal={closePackageModal}
                />
            </ModalLayout>
        </>
    );
};

export default PackageListing;
